import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const Globe = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    
    // Set renderer size to match window size
    const updateSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    
    updateSize();
    mountRef.current.appendChild(renderer.domElement);

    // Add window resize listener
    window.addEventListener('resize', updateSize);

    // Create wireframe sphere
    const geometry = new THREE.SphereGeometry(7.2, 32, 32);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00d4ff,
      wireframe: true,
      transparent: true,
      opacity: 0.3
    });
    const sphere = new THREE.Mesh(geometry, material);
    scene.add(sphere);

    // Add glow effect
    const glowGeometry = new THREE.SphereGeometry(7.5, 32, 32);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x00d4ff,
      transparent: true,
      opacity: 0.1
    });
    const glowSphere = new THREE.Mesh(glowGeometry, glowMaterial);
    scene.add(glowSphere);

    // Add connection points
    const pointsGeometry = new THREE.BufferGeometry();
    const pointsMaterial = new THREE.PointsMaterial({
      color: 0x00d4ff,
      size: 0.15,
      transparent: true,
      opacity: 0.8,
      sizeAttenuation: true,
      depthWrite: false,
      depthTest: false,  // Disable depth testing to ensure points are always visible
      blending: THREE.AdditiveBlending  // Add additive blending for better visibility
    });

    // Create points array with fixed positions
    const points = [];
    const pointsArray = [];
    const numPoints = 100;
    
    // Use a fixed seed for consistent point distribution
    let seed = 12345;
    function seededRandom(min, max) {
      // Linear congruential generator
      seed = (seed * 9301 + 49297) % 233280;
      const rnd = seed / 233280;
      return min + rnd * (max - min);
    }

    for (let i = 0; i < numPoints; i++) {
      const theta = seededRandom(0, Math.PI * 2);
      const phi = Math.acos(seededRandom(-1, 1));
      const x = 7.2 * Math.sin(phi) * Math.cos(theta);
      const y = 7.2 * Math.sin(phi) * Math.sin(theta);
      const z = 7.2 * Math.cos(phi);
      points.push(x, y, z);
      pointsArray.push(new THREE.Vector3(x, y, z));
    }

    pointsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
    const pointsMesh = new THREE.Points(pointsGeometry, pointsMaterial);
    scene.add(pointsMesh);

    // Tracer lines system
    const tracerLines = [];
    const maxLines = 10;
    const lineLifetime = 5000;
    const lineGeometry = new THREE.BufferGeometry();
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0x00d4ff,
      transparent: true,
      opacity: 0.4
    });

    // Function to create a new tracer line
    const createTracerLine = () => {
      if (tracerLines.length >= maxLines) return;

      // Pick two random points
      const startPoint = pointsArray[Math.floor(Math.random() * pointsArray.length)];
      const endPoint = pointsArray[Math.floor(Math.random() * pointsArray.length)];

      // Create line geometry
      const lineGeometry = new THREE.BufferGeometry().setFromPoints([startPoint, endPoint]);
      const line = new THREE.Line(lineGeometry, lineMaterial.clone());
      line.material.opacity = 0.4;
      line.createdAt = Date.now();
      scene.add(line);
      tracerLines.push(line);
    };

    // Function to update tracer lines
    const updateTracerLines = () => {
      const now = Date.now();
      for (let i = tracerLines.length - 1; i >= 0; i--) {
        const line = tracerLines[i];
        const age = now - line.createdAt;
        
        if (age > lineLifetime) {
          scene.remove(line);
          line.geometry.dispose();
          line.material.dispose();
          tracerLines.splice(i, 1);
        } else {
          // Fade out based on age
          const fadeProgress = age / lineLifetime;
          line.material.opacity = 0.4 * (1 - fadeProgress);
        }
      }
    };

    // Position camera
    camera.position.z = 20;

    // Animation
    let rotationSpeed = 0.00005;
    let pulseSpeed = 0.0010;
    let pulseDirection = 1;
    let pulseValue = 0.275;
    let lastTracerTime = 0;
    const tracerInterval = 500;

    const animate = () => {
      requestAnimationFrame(animate);

      // Rotate sphere
      sphere.rotation.y += rotationSpeed;
      glowSphere.rotation.y += rotationSpeed;
      pointsMesh.rotation.y += rotationSpeed;

      // Rotate tracer lines
      tracerLines.forEach(line => {
        line.rotation.y += rotationSpeed;
      });

      // Create new tracer lines
      const now = Date.now();
      if (now - lastTracerTime > tracerInterval) {
        createTracerLine();
        lastTracerTime = now;
      }

      // Update tracer lines
      updateTracerLines();

      // Pulse effect - only affect sphere and glow, not points
      pulseValue += pulseSpeed * pulseDirection;
      if (pulseValue >= 0.29 || pulseValue <= 0.26) {
        pulseDirection *= -1;
      }
      material.opacity = pulseValue;
      glowMaterial.opacity = pulseValue * 0.3;

      renderer.render(scene, camera);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateSize);
      if (mountRef.current && renderer.domElement.parentNode === mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
      scene.remove(sphere);
      scene.remove(glowSphere);
      scene.remove(pointsMesh);
      geometry.dispose();
      material.dispose();
      glowGeometry.dispose();
      glowMaterial.dispose();
      pointsGeometry.dispose();
      pointsMaterial.dispose();
      
      // Clean up tracer lines
      tracerLines.forEach(line => {
        scene.remove(line);
        line.geometry.dispose();
        line.material.dispose();
      });
    };
  }, []);

  return (
    <div 
      ref={mountRef} 
      className="globe-container"
      aria-label="Animated 3D wireframe globe representing global compliance screening"
    />
  );
};

export default Globe; 